import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Text } from '@actual-app/components/text';
import { View } from '@actual-app/components/view';

import { useAuth } from '#auth/AuthProvider';
import { Permissions } from '#auth/types';
import { useTravesia } from '#components/mobile/travesia/TravesiaContext';
import { Boton } from '#components/mobile/ui/Boton';
import { Hoja } from '#components/mobile/ui/Hoja';
import { color } from '#components/mobile/ui/tokens';

const CLAVE_VISTO = 'rumbo:prueba:datos-inventados';

/** ¿Falta enseñar el aviso en este dispositivo? */
export function avisoDatosPendiente(): boolean {
  try {
    return window.localStorage.getItem(CLAVE_VISTO) !== 'visto';
  } catch {
    return false;
  }
}

function marcarAvisoVisto() {
  try {
    window.localStorage.setItem(CLAVE_VISTO, 'visto');
  } catch {
    // Sin almacenamiento volverá a salir la próxima vez: no pasa nada.
  }
}

/**
 * Beta (G5): mientras no haya cifrado de extremo a extremo, el administrador
 * del servidor podría ver los presupuestos. Una vez por dispositivo, a quien
 * no es admin, se pide usar datos inventados. Sale antes que el permiso de
 * uso y nunca durante la primera travesía; cualquier forma de cerrarla cuenta
 * como vista.
 */
export function AvisoDatosInventados({
  onCerrado,
}: {
  onCerrado?: () => void;
}) {
  const travesiaActiva = !!useTravesia()?.activa;
  const { hasPermission } = useAuth();
  const esAdmin = hasPermission(Permissions.ADMINISTRATOR);
  const [visible, setVisible] = useState(avisoDatosPendiente);

  function cerrar() {
    marcarAvisoVisto();
    setVisible(false);
    onCerrado?.();
  }

  // Al admin no le sale, pero se da por visto para que el permiso de uso y
  // las novedades (que esperan a este aviso) no se queden esperando.
  useEffect(() => {
    if (visible && esAdmin) {
      cerrar();
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- solo al cambiar
  }, [visible, esAdmin]);

  if (!visible || travesiaActiva || esAdmin) {
    return null;
  }

  return <HojaDatosInventados onCerrar={cerrar} />;
}

/** La hoja en sí (sin estado), para poder probarla y hacerle capturas. */
export function HojaDatosInventados({ onCerrar }: { onCerrar: () => void }) {
  const { t } = useTranslation();
  return (
    <Hoja
      aria-label={t('Use made-up data in this trial')}
      data-testid="aviso-datos-inventados"
      onCerrar={onCerrar}
    >
      <View style={{ gap: 12, paddingTop: 6 }}>
        <Text
          role="heading"
          aria-level={2}
          style={{
            fontSize: 21,
            fontWeight: 800,
            letterSpacing: '-0.02em',
            lineHeight: 1.2,
            color: color.fg,
          }}
        >
          <Trans>Use made-up data in this trial</Trans>
        </Text>
        <Text style={{ fontSize: 15, lineHeight: 1.45, color: color.fg2 }}>
          <Trans>
            Rumbo is being tested. Your budget is not end-to-end encrypted yet,
            so the person who runs the server could see it. Please do not enter
            your real accounts or amounts.
          </Trans>
        </Text>
        <Boton
          bloque
          data-testid="aviso-datos-inventados-ok"
          onPress={onCerrar}
        >
          <Trans>Got it</Trans>
        </Boton>
      </View>
    </Hoja>
  );
}
